import { Box, Typography } from '@mui/material'
import { ErrorContainer, StyledForm } from './LoginPage.styled'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store/store'
import { loginUser, resetAuthError } from 'store'
import { AuthLogo, Button, Link, LoginInput } from 'components'
import EmailIcon from 'assets/svgComponents/EmailIcon'
import KeyIcon from 'assets/svgComponents/KeyIcon'
import { getDefaultRoute, isObjectEmpty, LoginValidationSchema } from 'utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

export const LoginPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    error: asyncError,
    passwordExpired,
    otpToken,
  } = useSelector((state: RootState) => state.auth)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(LoginValidationSchema) })

  const onSubmit = async (data) => {
    const result = await dispatch(loginUser(data))

    if (loginUser.fulfilled.match(result)) {
      const { requiresTOU } = result.payload

      if (requiresTOU) {
        navigate('/auth/terms-of-use')
        return
      }

      const redirectPath = getDefaultRoute()
      navigate(redirectPath)
    }
  }

  const isFormValid = useMemo(() => isObjectEmpty(errors), [errors])

  return (
    <Box
      width="360px"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap="40px"
    >
      <AuthLogo />
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column">
          <Controller
            name="email"
            control={control}
            defaultValue={''}
            render={({ field }) => (
              <LoginInput
                placeholder="Email"
                error={!!errors['email']}
                preIcon={<EmailIcon />}
                cut="top"
                {...field}
                onChange={(e) => {
                  field.onChange(e)
                  dispatch(resetAuthError())
                }}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            defaultValue={''}
            render={({ field }) => (
              <LoginInput
                placeholder="Password"
                type="password"
                error={!!errors['password']}
                cut="bottom"
                preIcon={<KeyIcon />}
                {...field}
                onChange={(e) => {
                  field.onChange(e)
                  dispatch(resetAuthError())
                }}
              />
            )}
          />
        </Box>
        <ErrorContainer>
          {!isFormValid ? (
            <>
              <Typography>{errors['email']?.message}</Typography>
              <Typography>{errors['password']?.message}</Typography>
            </>
          ) : null}
          {asyncError && <Typography>{asyncError}</Typography>}
        </ErrorContainer>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Link to="/auth/forgot-password">Forgot password?</Link>
          {passwordExpired && otpToken ? (
            <Button
              type="button"
              width="200px"
              onClick={() => {
                window.location.href = `/auth/reset-password/${otpToken}`
              }}
            >
              Reset Password
            </Button>
          ) : (
            <Button type="submit" width="150px">
              Login
            </Button>
          )}
        </Box>
      </StyledForm>
    </Box>
  )
}
